/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * What this cart actually costs, tax included — asked of the server, never
 * worked out here.
 *
 * WHY THE PAGE DOES NOT COMPUTE TAX. Which head of tax applies (CGST + SGST,
 * or IGST) depends on the state the event is held in versus the state the
 * supplier is registered in. Both of those are server-side facts, and getting
 * the head wrong misfiles the tax rather than merely misprinting it. It is the
 * same argument that demoted the client's `amount` to a claim to be checked —
 * see buildCheckoutPayload's price_ref_id note — so the figure on screen comes
 * from the same place the charge will.
 *
 * THE ONLY FILE THAT KNOWS THE WIRE FORMAT. Everything downstream reads the
 * shape `normaliseQuote` returns, so when the endpoint's body changes, this
 * file changes and nothing else does.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * THE CONTRACT THIS EXPECTS (and therefore the spec the endpoint implements)
 *
 *   POST /arcee/payments/quote/
 *   {
 *     application_id,
 *     coupon_code?,                     // omitted when none is applied
 *     items: [{ amount, chargeable_entity_code, chargeable_entity_parent_id }]
 *   }
 *
 *   200 {
 *     gross_amount, discount_amount, taxable_amount, tax_total, payable_amount,
 *     place_of_supply,                  // "Karnataka"
 *     supply_type,                      // intra_state | inter_state | exempt
 *     exempt_upto_amount,               // the per-seat threshold, or null
 *     tax: [{ code, label, percent, amount }],
 *     coupon: { applicable, code?, reason?, max_discount_amount? } | null
 *   }
 *
 * Amounts arrive as strings, as every other payments endpoint sends them.
 * ──────────────────────────────────────────────────────────────────────────
 */

import APIHelper from '../../../api/APIHelper';
import envConfig from '../../../config/envConfig';

const api = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

export const QUOTE_ENDPOINT = '/arcee/payments/quote/';

/** A money string from the server as a number. Missing or unparseable reads as 0. */
function money(value) {
  if (value === undefined || value === null || value === '') return 0;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

/**
 * How one tax line is worded.
 *
 * The server's own label is preferred, because the wording of a tax head is a
 * compliance question and not a styling one — "CGST @ 9%" is what has to
 * appear on the invoice too. Derived only when it sent none, so a new head
 * (UTGST, a cess) renders sensibly without a change here.
 */
function labelFor(component) {
  if (component?.label) return component.label;
  const code = component?.code || 'Tax';
  const percent = component?.percent;
  return percent ? `${code} @ ${Number(percent)}%` : code;
}

/**
 * The server's body in the shape the UI reads.
 *
 * Returns null for a body that carries no payable figure at all, which is the
 * one thing every caller needs; a quote that cannot say what is owed is not a
 * quote, and rendering a summary from it would print zeroes beside a real
 * charge.
 *
 * TOLERANT ABOUT TAX, STRICT ABOUT TOTALS. A body with no `tax` array is a
 * perfectly good answer — it is what a cart under the exemption threshold
 * gets, and what the endpoint returns before tax rules are configured at all.
 * It normalises to an empty component list, which renders as no tax rows.
 */
export function normaliseQuote(wire) {
  if (!wire || typeof wire !== 'object') return null;
  if (wire.payable_amount === undefined || wire.payable_amount === null) return null;

  const components = (Array.isArray(wire.tax) ? wire.tax : [])
    // A zero-valued head is dropped rather than printed as "CGST @ 9% ₹0.00",
    // which reads as a bug to anybody who can add up.
    .filter(component => money(component?.amount) > 0)
    .map(component => ({
      code: component.code || null,
      label: labelFor(component),
      percent: component.percent !== undefined && component.percent !== null
        ? Number(component.percent) : null,
      amount: money(component.amount),
    }));

  return {
    gross: money(wire.gross_amount),
    discount: money(wire.discount_amount),
    taxable: money(wire.taxable_amount),
    taxTotal: money(wire.tax_total),
    payable: money(wire.payable_amount),
    components,
    placeOfSupply: wire.place_of_supply || null,
    supplyType: wire.supply_type || null,
    // Null when the server declares no exemption. Kept as the server's figure
    // and never defaulted here: a threshold hard-coded in the page would go on
    // claiming "₹499" after the rule behind the charge had moved.
    exemptUpto: wire.exempt_upto_amount === undefined || wire.exempt_upto_amount === null
      ? null : money(wire.exempt_upto_amount),

    // WHETHER THE SERVER COULD WORK THE TAX OUT AT ALL.
    //
    // A cart it cannot tax still comes back 200 with usable pre-tax figures —
    // a missing event state is an ordinary answer, not an outage. But those
    // figures are NOT a price anybody can be charged: checkout refuses the
    // same cart for the same reason. Without carrying this through, the page
    // would show a clean pre-tax total and a Pay button for a basket that
    // cannot be paid for, and the runner would meet the refusal at the one
    // moment they had committed.
    taxDetermined: wire.taxable !== false,
    taxError: wire.tax_error
      ? {
        reason: wire.tax_error.reason || null,
        // The server's own sentence. It names the thing to fix — "the event
        // has no state recorded" — which no wording invented here could.
        detail: wire.tax_error.detail || null,
      }
      : null,
  };
}

/**
 * Prices one cart. Writes nothing.
 *
 * Returns the normalised quote, or null when the call could not be made —
 * which every caller must treat as "we do not know the tax yet" and fall back
 * to the pre-tax figures, NOT as "there is no tax". The difference matters:
 * the first shows a runner the price they have always been shown, the second
 * would promise them a total the gateway is about to disagree with.
 */
export async function fetchCheckoutQuote({
  applicationId, items, couponCode,
} = {}) {
  try {
    const resp = await api.apiCall(QUOTE_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        application_id: Number(applicationId ?? envConfig.applicationId),
        // Omitted entirely when no code is applied, so a cart without a coupon
        // sends the body it would have sent before coupons existed.
        ...(couponCode ? { coupon_code: couponCode } : {}),
        items,
      }),
    });
    if (!resp.ok) return null;
    return normaliseQuote(await resp.json());
  } catch (err) {
    return null;
  }
}
